import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';
import type {
  ProductVariant,
  VariantMatrixQuery,
  VariantMatrixResponse,
  BulkCreateVariantsDto,
  BulkUpdateVariantsDto,
  ProductImage,
} from '../types';

// DTO for creating a variant
export interface CreateVariantDto {
  sku: string;
  attributes: Record<string, unknown>;
  priceAdjustment: number;
  images?: ProductImage[];
  isActive?: boolean;
}

// DTO for updating a variant
export interface UpdateVariantDto {
  sku?: string;
  attributes?: Record<string, unknown>;
  priceAdjustment?: number;
  images?: ProductImage[];
  isActive?: boolean;
}

// API functions for variant operations
export const variantApi = {
  // Fetch all variants for a product
  getVariants: async (productId: string): Promise<ProductVariant[]> => {
    const response = await axiosInstance.get<ProductVariant[]>(`/products/${productId}/variants`);
    return response.data;
  },

  // Fetch a single variant
  getVariant: async (productId: string, variantId: string): Promise<ProductVariant> => {
    const response = await axiosInstance.get<ProductVariant>(
      `/products/${productId}/variants/${variantId}`
    );
    return response.data;
  },

  // Create a new variant
  createVariant: async (productId: string, data: CreateVariantDto): Promise<ProductVariant> => {
    const response = await axiosInstance.post<ProductVariant>(
      `/products/${productId}/variants`,
      data
    );
    return response.data;
  },

  // Update a variant
  updateVariant: async (
    productId: string,
    variantId: string,
    data: UpdateVariantDto
  ): Promise<ProductVariant> => {
    const response = await axiosInstance.patch<ProductVariant>(
      `/products/${productId}/variants/${variantId}`,
      data
    );
    return response.data;
  },

  // Delete a variant
  deleteVariant: async (productId: string, variantId: string): Promise<void> => {
    await axiosInstance.delete(`/products/${productId}/variants/${variantId}`);
  },

  // Bulk create variants
  bulkCreateVariants: async (
    productId: string,
    data: BulkCreateVariantsDto
  ): Promise<ProductVariant[]> => {
    const response = await axiosInstance.post<ProductVariant[]>(
      `/products/${productId}/variants/bulk`,
      data
    );
    return response.data;
  },

  // Bulk update variants
  bulkUpdateVariants: async (
    productId: string,
    data: BulkUpdateVariantsDto
  ): Promise<ProductVariant[]> => {
    const response = await axiosInstance.patch<ProductVariant[]>(
      `/products/${productId}/variants/bulk-update`,
      data
    );
    return response.data;
  },

  // Get variant matrix view
  getVariantMatrix: async (
    productId: string,
    params: VariantMatrixQuery
  ): Promise<VariantMatrixResponse> => {
    const response = await axiosInstance.get<VariantMatrixResponse>(
      `/products/${productId}/variants/matrix`,
      { params }
    );
    return response.data;
  },
};

// Fetch all variants for a product
export const useVariants = (productId: string) => {
  return useQuery({
    queryKey: ['products', productId, 'variants'],
    queryFn: () => variantApi.getVariants(productId),
    enabled: !!productId,
  });
};

// Fetch a single variant
export const useVariant = (productId: string, variantId: string) => {
  return useQuery({
    queryKey: ['products', productId, 'variants', variantId],
    queryFn: () => variantApi.getVariant(productId, variantId),
    enabled: !!productId && !!variantId,
  });
};

// Fetch variant matrix view
export const useVariantMatrix = (productId: string, params: VariantMatrixQuery) => {
  return useQuery({
    queryKey: ['products', productId, 'variants', 'matrix', params],
    queryFn: () => variantApi.getVariantMatrix(productId, params),
    enabled: !!productId && !!params.rows && !!params.cols,
  });
};

// Create a variant mutation
export const useCreateVariant = (productId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateVariantDto) => variantApi.createVariant(productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', productId, 'variants'] });
      queryClient.invalidateQueries({ queryKey: ['products', productId] });
    },
  });
};

// Update a variant mutation
export const useUpdateVariant = (productId: string, variantId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateVariantDto) => variantApi.updateVariant(productId, variantId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', productId, 'variants'] });
      queryClient.invalidateQueries({ queryKey: ['products', productId, 'variants', variantId] });
      queryClient.invalidateQueries({ queryKey: ['products', productId] });
    },
  });
};

// Delete a variant mutation
export const useDeleteVariant = (productId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variantId: string) => variantApi.deleteVariant(productId, variantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', productId, 'variants'] });
      queryClient.invalidateQueries({ queryKey: ['products', productId] });
    },
  });
};

// Bulk create variants mutation
export const useBulkCreateVariants = (productId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkCreateVariantsDto) => variantApi.bulkCreateVariants(productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', productId, 'variants'] });
      queryClient.invalidateQueries({ queryKey: ['products', productId] });
    },
  });
};

// Bulk update variants mutation
export const useBulkUpdateVariants = (productId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkUpdateVariantsDto) => variantApi.bulkUpdateVariants(productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', productId, 'variants'] });
      queryClient.invalidateQueries({ queryKey: ['products', productId] });
    },
  });
};
